import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import Plan, { UserPlan } from '~/types/Plan';
import { getFailed, getNotRequested, getRequesting, getSuccess } from '~/utils/fetchable';

export type PlansState = {
  currentPlan: Readonly<Maybe<UserPlan>>;
  fetchPlanRequest: Requestable<UserPlan, string>;
  fetchPricingPlansRequest: Requestable<Plan[], string>;
  setPlanRequest: Requestable<Maybe<UserPlan>, string>;
  nextPlan: Readonly<Maybe<UserPlan>>;
  hasClosedWixProPlanNotification: Readonly<boolean>;
  closeAccountModalIsOpen: Readonly<boolean>;
  closeAccountError: Maybe<string>;
  adjustedPlan: Plan | null;
};

export const initialState: PlansState = {
  currentPlan: null,
  fetchPlanRequest: getNotRequested(),
  fetchPricingPlansRequest: getNotRequested(),
  setPlanRequest: getNotRequested(),
  nextPlan: null,
  hasClosedWixProPlanNotification: false,
  closeAccountModalIsOpen: false,
  closeAccountError: null,
  adjustedPlan: null,
};

function plansReducer(state: PlansState = initialState, action: ActionTypes): PlansState {
  switch (action.type) {
    case ActionKeys.FETCH_USER_PLANS:
      return {
        ...state,
        fetchPlanRequest: getRequesting(),
      };
    case ActionKeys.FETCH_USER_PLANS_SUCCESS:
      const now = Date();
      // The backend only returns the current plan and the next plan if any
      const currentPlan = action.payload.userPlans.filter(
        (plan) =>
          plan.startDate.isSameOrBefore(now) && (!plan.endDate || plan.endDate.isAfter(now)),
      );
      const nextPlan = action.payload.userPlans.filter((plan) => plan.startDate.isAfter(now));

      return {
        ...state,
        currentPlan: currentPlan[0] || null,
        nextPlan: nextPlan[0] || null,
        fetchPlanRequest: getSuccess(currentPlan[0]),
      };
    case ActionKeys.FETCH_USER_PLANS_ERROR:
      return {
        ...state,
        fetchPlanRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.FETCH_PRICING_PLANS:
      return {
        ...state,
        fetchPricingPlansRequest: getRequesting(),
      };
    case ActionKeys.FETCH_PRICING_PLANS_SUCCESS:
      return {
        ...state,
        fetchPricingPlansRequest: getSuccess(action.payload.pricingPlans),
      };
    case ActionKeys.FETCH_PRICING_PLANS_ERROR:
      return {
        ...state,
        fetchPricingPlansRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.UPDATE_USER_PLAN:
      return {
        ...state,
        setPlanRequest: getRequesting(),
      };
    case ActionKeys.UPDATE_USER_PLAN_ERROR:
      return {
        ...state,
        setPlanRequest: getFailed(action.payload.errorMessage),
      };
    case ActionKeys.UPDATE_USER_PLAN_SUCCESS:
      return {
        ...state,
        setPlanRequest: getSuccess(action.payload.userPlan),
      };
    case ActionKeys.RESET_USER_PLAN_REQUEST:
      return {
        ...state,
        setPlanRequest: getNotRequested(),
      };
    case ActionKeys.CLOSE_WIX_PRO_PLAN_NOTIFICATION:
      return {
        ...state,
        hasClosedWixProPlanNotification: true,
      };
    case ActionKeys.OPEN_CLOSE_ACCOUNT_MODAL:
      return {
        ...state,
        closeAccountModalIsOpen: true,
      };
    case ActionKeys.DISMISS_CLOSE_ACCOUNT_MODAL:
      return {
        ...state,
        closeAccountModalIsOpen: false,
        closeAccountError: null,
      };
    case ActionKeys.SUBMIT_CLOSE_ACCOUNT_FORM:
    case ActionKeys.CLOSE_ACCOUNT_SUCCESS:
      return {
        ...state,
        closeAccountError: null,
      };
    case ActionKeys.CLOSE_ACCOUNT_ERROR:
      return {
        ...state,
        closeAccountError: action.payload.errorMessage,
      };
    case ActionKeys.SET_ADJUSTED_PLAN:
      return {
        ...state,
        adjustedPlan: action.payload.plan,
      };
    default:
      return state;
  }
}

export default plansReducer;
